import type { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import i18n from 'i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import Depth from '@/assets/icons/depth.svg?component';
import Lice from '@/assets/icons/louse.svg?component';
import Bioscope from '@/assets/icons/operational-dashboard/bioscope.svg?component';
import BioscopeLux from '@/assets/icons/operational-dashboard/bioscope-lux.svg?component';
import CircleCheck from '@/assets/icons/operational-dashboard/circle-check.svg?component';
import CircleClose from '@/assets/icons/operational-dashboard/circle-close.svg?component';
import PitchRoll from '@/assets/icons/operational-dashboard/pitch-roll.svg?component';
import Roll from '@/assets/icons/operational-dashboard/roll.svg?component';
import Weight from '@/assets/icons/operational-dashboard/weight.svg?component';
import { Badge, type BadgeTone } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import Sparkline from '@/components/atoms/Sparkline/Sparkline';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { TextLink } from '@/components/atoms/TextLink';
import Tooltip from '@/components/atoms/Tooltip';
import { usePermissions } from '@/contexts/auth/hooks';
import { useLiveBioscopeData } from '@/contexts/mqtt/useLiveBioscopeData';
import type { DepthPoint } from '@/domain/depthSeries';
import type { PenOverviewItem, PenStatus } from '@/domain/operationalDashboard';
import { formatDateTimeWorded } from '@/utils/date';
import { formatNumberByLocale, formatPercentageByLocale } from '@/utils/localeFormatters';

import { depthChange, MS_PER_24_HOURS, pointsInLast24Hours, timedDepthPoints } from './penDepth';
import { buildPenMetricTooltip } from './penMetricTooltip';

import styles from './PenOverviewCard.module.scss';

type IconComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

interface PenOverviewCardProps {
    pen: PenOverviewItem;
    /** Set while hourly depth is on. Absent hides the sparkline. */
    depthPoints?: DepthPoint[];
    /** Site timezone. The depth hours are this clock. */
    timeZone?: string;
}

const STATUS_CARD_CLASS: Record<PenStatus, string> = {
    all_good: styles.statusAllGood,
    warning: styles.statusWarning,
    critical: styles.statusCritical,
    offline: styles.statusOffline,
};

const STATUS_BADGE_TONE: Record<PenStatus, BadgeTone> = {
    all_good: 'success',
    warning: 'warning',
    critical: 'danger',
    offline: 'danger',
};

const STATUS_BADGE_ICON: Record<PenStatus, IconComponent | undefined> = {
    all_good: CircleCheck,
    warning: undefined,
    critical: undefined,
    offline: CircleClose,
};

export default function PenOverviewCard(props: PenOverviewCardProps) {
    const { pen, depthPoints, timeZone } = props;
    const { t } = useTranslation();
    const { clientId, locationId } = useParams<{ clientId: string; locationId: string }>();
    const { isOptoScaleAdmin } = usePermissions();

    const live = useLiveBioscopeData(pen.bioscopeId);

    const cageBaseUrl = `/c/${clientId}/location/${locationId}/pen/${pen.cageId}`;
    const cageDashboardUrl = `${cageBaseUrl}/dashboard`;
    const bioscopeStatusUrl = `${cageBaseUrl}/dashboard/bioscope`;
    const weightUrl = `${cageBaseUrl}/weight`;
    const liceUrl = `${cageBaseUrl}/lice`;

    // Status badge: live MQTT online flag wins when known; server-derived fallback
    // before the retained `online` byte arrives.
    const liveBadge: { tone: BadgeTone; icon: IconComponent; label: string } | null =
        live.online === true
            ? { tone: 'success', icon: CircleCheck, label: t('common.labels.online') }
            : live.online === false
              ? { tone: 'danger', icon: CircleClose, label: t('common.labels.offline') }
              : null;
    const fallbackBadge = {
        tone: STATUS_BADGE_TONE[pen.status],
        icon: STATUS_BADGE_ICON[pen.status],
        label: t(`operationalDashboard.penStatus.${pen.status}`),
    };
    const badge = liveBadge ?? fallbackBadge;

    // Card left-border follows the live MQTT state when known, falls back to server status.
    const cardStatusClass =
        live.online === true
            ? styles.statusOnline
            : live.online === false
              ? styles.statusOffline
              : STATUS_CARD_CLASS[pen.status];

    const showDepthGraph = depthPoints !== undefined;
    const now = new Date();
    const recentDepth =
        showDepthGraph && timeZone ? pointsInLast24Hours(depthPoints, now, timeZone) : [];
    const delta = showDepthGraph ? depthChange(recentDepth) : null;
    const sparklinePoints = timeZone ? timedDepthPoints(recentDepth, timeZone) : [];

    const lastMeasurementStr = pen.lastMeasurement
        ? formatDateTimeWorded(pen.lastMeasurement)
        : t('operationalDashboard.pen.noData');

    const tooltipArgs = { isAdmin: isOptoScaleAdmin, t };

    // Weight measurement count tooltip — only when today's batch row exists.
    const weightTooltip = pen.weightMeasurementCountAsOf
        ? buildPenMetricTooltip({
              ...tooltipArgs,
              sourceLabel: t('metricTooltip.sourceLabel.weightCountToday'),
              debugSource: {
                  table: 'daily_measurements',
                  column: 'cnt',
                  day: 'today (location TZ)',
              },
              lastUpdated: pen.weightMeasurementCountAsOf,
          })
        : undefined;

    // Lice measurement count tooltip — only when today's batch row exists.
    const liceTooltip = pen.liceMeasurementCountAsOf
        ? buildPenMetricTooltip({
              ...tooltipArgs,
              sourceLabel: t('metricTooltip.sourceLabel.liceCountToday'),
              debugSource: {
                  table: 'daily_lice_count',
                  column: 'cnt',
                  day: 'today (location TZ)',
              },
              lastUpdated: pen.liceMeasurementCountAsOf,
          })
        : undefined;

    // Depth + lux share the same MQTT message timestamp
    const mqttTooltip = live.lastMessageAt
        ? buildPenMetricTooltip({
              ...tooltipArgs,
              sourceLabel: t('metricTooltip.sourceLabel.mqtt'),
              debugSource: { topic: `bioscope/data/response/${pen.bioscopeId}` },
              lastUpdated: live.lastMessageAt,
          })
        : undefined;

    // Show MQTT-sourced metrics only when the bioscope is confirmed online and has
    // sent a value. All four show '-' when offline or no MQTT signal yet.
    const isKnownOffline = live.online === false;
    const depthValue = !isKnownOffline ? live.depth : null;
    const depthTooltip = !isKnownOffline && live.depth != null ? mqttTooltip : undefined;
    const luxTooltip = !isKnownOffline && live.lux != null ? mqttTooltip : undefined;
    const pitchValue = !isKnownOffline ? live.pitch : null;
    const pitchTooltip = !isKnownOffline && live.pitch != null ? mqttTooltip : undefined;
    const rollValue = !isKnownOffline ? live.roll : null;
    const rollTooltip = !isKnownOffline && live.roll != null ? mqttTooltip : undefined;

    return (
        <Card
            radius="sm"
            accent={cardStatusClass ? 'stripe' : undefined}
            className={classNames(styles.card, cardStatusClass)}>
            <div className={styles.header}>
                <div className={styles.penTitle}>
                    <span className={styles.penLabel}>{t('operationalDashboard.pen.label')}</span>
                    <TextLink tone="quiet" to={cageDashboardUrl} className={styles.penName}>
                        {pen.cageName}
                    </TextLink>
                </div>
                <Badge
                    tone={badge.tone}
                    icon={
                        badge.icon && (
                            <SvgIcon
                                icon={badge.icon}
                                size="custom"
                                customSize={14}
                                color="currentColor"
                            />
                        )
                    }>
                    {badge.label}
                </Badge>
            </div>
            <div className={styles.metricsRow}>
                <Tooltip content={weightTooltip} placement="top">
                    <PenMetricInner
                        url={weightUrl}
                        icon={Weight}
                        label={
                            pen.weightMeasurementCount != null
                                ? formatNumberByLocale(pen.weightMeasurementCount, i18n.language, 0)
                                : '-'
                        }
                    />
                </Tooltip>
                <Tooltip content={liceTooltip} placement="top">
                    <PenMetricInner
                        url={liceUrl}
                        icon={Lice}
                        label={
                            pen.liceMeasurementCount != null
                                ? formatNumberByLocale(pen.liceMeasurementCount, i18n.language, 0)
                                : '-'
                        }
                    />
                </Tooltip>
                <Tooltip content={depthTooltip} placement="top">
                    <PenMetricInner
                        url={bioscopeStatusUrl}
                        icon={Depth}
                        // The metric row is too narrow at phone width for a spaced unit, which wraps.
                        label={
                            depthValue != null
                                ? `${formatNumberByLocale(depthValue, i18n.language, 1)}m`
                                : '-'
                        }
                    />
                </Tooltip>
                <Tooltip content={luxTooltip} placement="top">
                    <PenMetricInner
                        url={bioscopeStatusUrl}
                        icon={BioscopeLux}
                        label={
                            isKnownOffline
                                ? '-'
                                : formatPercentageByLocale(live.lux, i18n.language, 0)
                        }
                    />
                </Tooltip>
                <Tooltip content={pitchTooltip} placement="top">
                    <PenMetricInner
                        url={bioscopeStatusUrl}
                        icon={PitchRoll}
                        label={
                            pitchValue != null
                                ? t('operationalDashboard.penOverview.pitchValue', {
                                      value: formatNumberByLocale(pitchValue, i18n.language, 1),
                                  })
                                : '-'
                        }
                    />
                </Tooltip>
                <Tooltip content={rollTooltip} placement="top">
                    <PenMetricInner
                        url={bioscopeStatusUrl}
                        icon={Roll}
                        label={
                            rollValue != null
                                ? t('operationalDashboard.penOverview.rollValue', {
                                      value: formatNumberByLocale(rollValue, i18n.language, 1),
                                  })
                                : '-'
                        }
                    />
                </Tooltip>
            </div>
            {showDepthGraph && (
                <div className={styles.depthRow}>
                    <div className={styles.depthSummary}>
                        <Sparkline
                            points={sparklinePoints}
                            from={now.getTime() - MS_PER_24_HOURS}
                            to={now.getTime()}
                        />
                        <span>
                            <span className={styles.depthLabel}>
                                {t('operationalDashboard.penOverview.depthLast24h')}
                            </span>{' '}
                            {delta == null
                                ? '-'
                                : `${delta > 0 ? '+' : ''}${formatNumberByLocale(delta, i18n.language, 2)} m`}
                        </span>
                    </div>
                    <Button
                        variant="secondary"
                        size="small"
                        className={styles.depthGraph}
                        to={bioscopeStatusUrl}
                        iconEnd={<SvgIcon icon={ArrowRightLine} color="currentColor" />}>
                        {t('operationalDashboard.penOverview.depthGraph')}
                    </Button>
                </div>
            )}
            <div className={styles.separator} />
            <div className={styles.bioscopeFooter}>
                <div className={styles.lastMeasurementBlock}>
                    <span className={styles.lastMeasurementLabel}>
                        {t('operationalDashboard.pen.lastMeasurement')}
                    </span>
                    <span className={styles.lastMeasurementValue}>{lastMeasurementStr}</span>
                </div>
                {pen.bioscopeId && (
                    <TextLink tone="quiet" to={bioscopeStatusUrl} className={styles.bioscopeId}>
                        <SvgIcon
                            icon={Bioscope}
                            size="custom"
                            customSize={18}
                            color="var(--color-text-secondary)"
                        />
                        {pen.bioscopeId}
                    </TextLink>
                )}
            </div>
        </Card>
    );
}

interface PenMetricInnerProps {
    url: string;
    icon: IconComponent;
    label: string;
}

function PenMetricInner(props: PenMetricInnerProps) {
    const { url, icon, label } = props;

    return (
        <TextLink tone="quiet" to={url} className={styles.metric}>
            <SvgIcon
                icon={icon}
                size="custom"
                customSize={20}
                color="var(--color-text-secondary)"
            />
            {label}
        </TextLink>
    );
}
